/**
 * Reel page text. User-facing strings live here, never inline in the component.
 */

export const REEL = {
  TITLE: "Top 20 clips",
  SUBTITLE:
    "A short list of the most postable moments, picked automatically. Play them, tick the ones you want, export.",
  LOADING: "Finding your best moments…",
  LOAD_ERROR: "Couldn't load these clips.",
  EMPTY: "Nothing matched this recipe in this video. Try another one.",
  NO_TRANSCRIPT: "Open this page from a video in My Library.",
  BACK: "← My Library",
  OPEN_IN_ET: "Open the full spreadsheet",
  SELECT_ALL: "Select all",
  CLEAR: "Clear",
  EXPORT_ONE: "Export clip",
  EXPORTING: "Exporting…",
  PLAY: "Play clip",
  SELECTED_NONE: "Tick a clip to export it",
  LIBRARY_HINT: "Exports land in My Library → Exported.",
  SEND_TO_AUTO: "Send to Auto",
  SEND_TO_AUTO_HINT: "Off = the files are made but not pushed to your webhook.",
  BURN_CAPTIONS: "Captions",
  BURN_CAPTIONS_HINT: "Burn captions into each clip — three words at a time, the spoken word lit up (a few seconds more per render).",
  STYLE: "Style",
  STYLE_HINT: "Caption colours.",
  ORIENTATION: "Orientation",
  VIEW: "View",
  VIEW_SMALL: "Small",
  VIEW_LARGE: "Large",
  VERTICAL_PREVIEW: "Vertical 9:16, Blur — the whole picture, a blurred copy filling above and below. Click to export this clip this way",
  VERTICAL_ZOOM_PREVIEW: "Vertical 9:16, Zoom — the picture fills the screen, centered on the face when it exports. Click to export this clip this way",
  LOOK_ORIGINAL: "Original",
  LOOK_BLUR: "Blur",
  LOOK_ZOOM: "Zoom",
  PICK_ORIGINAL: "Export this clip in its original shape",
  ORIENTATION_HINT: "Original keeps the video's own shape. Vertical makes a 9:16 short for Shorts, Reels and TikTok: Blur shows the whole picture, Zoom fills the screen.",
} as const;

/** Caption palettes — the phone app's four (mobile/captions.js) plus the export window's
 *  extra colours, so a My Style saved there shows up here unchanged. */
export const CAPTION_STYLES = [
  { key: "cherry", label: "White & cherry" },
  { key: "allcherry", label: "All cherry" },
  { key: "white", label: "All white" },
  { key: "yellow", label: "White & yellow" },
  { key: "green", label: "White & green" },
  { key: "blue", label: "White & blue" },
  { key: "purple", label: "White & purple" },
  { key: "orange", label: "White & orange" },
  { key: "allyellow", label: "All yellow" },
  { key: "violet", label: "White & violet" },
  { key: "lime", label: "White & lime" },
] as const;

export type CaptionStyle = (typeof CAPTION_STYLES)[number]["key"];

/** The palette colours, same as the server's CAPTION_PALETTES — for the 9:16 preview. */
export const CAPTION_COLOURS: Record<CaptionStyle, { base: string; active: string }> = {
  cherry: { base: "#FFFFFF", active: "#EC2B57" },
  allcherry: { base: "#EC2B57", active: "#FFFFFF" },
  white: { base: "#FFFFFF", active: "#FFFFFF" },
  yellow: { base: "#FFFFFF", active: "#FFE000" },
  green: { base: "#FFFFFF", active: "#39E75F" },
  blue: { base: "#FFFFFF", active: "#2EA8FF" },
  purple: { base: "#FFFFFF", active: "#B26BFF" },
  orange: { base: "#FFFFFF", active: "#FF8A1F" },
  allyellow: { base: "#FFE000", active: "#FFFFFF" },
  violet: { base: "#FFFFFF", active: "#7B3FE4" },
  lime: { base: "#FFFFFF", active: "#C6FF3A" },
};

export type ViewSize = "small" | "large";

export const ORIENTATIONS = [
  { key: "original", label: "Original" },
  { key: "vertical", label: "Vertical 9:16 · Blur" },
  { key: "vertical_fill", label: "Vertical 9:16 · Zoom" },
] as const;

export type Orientation = (typeof ORIENTATIONS)[number]["key"];

/** "Export 5 clips" — each becomes its own video file. */
export const exportLabel = (count: number) =>
  count === 1 ? "Export 1 clip" : `Export ${count} clips`;

/** Progress while the queue drains, e.g. "Queued 3 of 7…". */
export const exportProgress = (done: number, total: number) =>
  `Queued ${done} of ${total}…`;

export const exportDone = (count: number) =>
  count === 1
    ? "1 clip is being made — it'll appear in My Library → Exported."
    : `${count} clips are being made — they'll appear in My Library → Exported.`;

export const exportFailed = (count: number) =>
  `${count} clip${count === 1 ? "" : "s"} couldn't be queued.`;
